import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemAvatar, ListItemText, Avatar, Chip, CircularProgress, Button } from '@mui/material';
import { Person, Add } from '@mui/icons-material';
import ModernButton from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const EmployeesWidget = ({ projectId, onSettings, onRemove }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      fetchEmployees();
    }
  }, [projectId]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/projects/${projectId}/employees`);
      console.log('Employees API response:', response.data);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        minHeight: '300px'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexShrink: 0
      }}>
        <Typography variant="body2" color="text.secondary">
          {employees.length} empleados
        </Typography>
        <ModernButton
          size="small"
          startIcon={<Add />}
          variant="outlined"
          onClick={() => window.location.href = '/facility/employments'}
        >
          Agregar
        </ModernButton>
      </Box>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}>
        {employees.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No se encontraron empleados
            </Typography>
            <ModernButton
              size="small"
              variant="text"
              onClick={() => window.location.href = '/facility/employments'}
            >
              Agregar primer empleado
            </ModernButton>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {employees.slice(0, 5).map((employee) => (
              <ListItem 
                key={employee.id} 
                sx={{ 
                  px: 0, 
                  py: 1,
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateX(4px)',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      width: 40,
                      height: 40
                    }}
                    src={employee.photo_url || employee.profile_image || employee.avatar_url}
                    alt={employee.name || 'Employee'}
                  >
                    {employee.name?.charAt(0) || employee.email?.charAt(0) || <Person />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={employee.name || 'Sin nombre'}
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {employee.email || 'Sin email'}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                        {employee.position || employee.role || 'Empleado'}
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{ 
                    variant: 'body2', 
                    sx: { fontWeight: 600 } 
                  }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Chip
                  label={employee.status || 'Activo'}
                  color={getStatusColor(employee.status)}
                  size="small"
                  sx={{ 
                    borderRadius: 2,
                    fontWeight: 500
                  }}
                />
              </ListItem>
            ))}
            {employees.length > 5 && (
              <ListItem sx={{ 
                px: 0, 
                py: 1,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <ListItemText
                  primary={`+${employees.length - 5} empleados más`}
                  primaryTypographyProps={{ 
                    variant: 'caption', 
                    color: 'text.secondary',
                    sx: { fontWeight: 500 }
                  }}
                />
                <ModernButton
                  size="small"
                  variant="text"
                  onClick={() => window.location.href = '/facility/employments'}
                >
                  Ver Todos
                </ModernButton>
              </ListItem>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default EmployeesWidget; 